import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { operatorAPI, formatNumber } from '../services/api';

// Import operator logos
import unitelLogo from '../assets/logos/unitellogo.png';
import ltcLogo from '../assets/logos/ltclogo.png';
import tplusLogo from '../assets/logos/tpluslogo.png';
import etlLogo from '../assets/logos/etllogo.png';
import bestLogo from '../assets/logos/bestlogo.png';

const OperatorSubscribers = ({ month, year }) => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [activeRate, setActiveRate] = useState(0);
  const monthParam = month != null ? month : new Date().getMonth() + 1;
  const yearParam = year != null ? year : new Date().getFullYear();

  // Logo mapping
  const logoMap = {
    'UNITEL': unitelLogo,
    'LTC': ltcLogo,
    'TPLUS': tplusLogo,
    'ETL': etlLogo,
    'BEST': bestLogo
  };

  // Enhanced operator color schemes with gradient and border colors
  const operatorColorSchemes = {
    'UNITEL': {
      primary: '#f6a83bff',
      gradient: 'from-blue-500 to-blue-600',
      border: 'border-black-300',
      hoverBorder: 'hover:border-blue-400',
      background: 'bg-gradient-to-br from-blue-50 to-blue-100',
      hoverBackground: 'hover:from-blue-100 hover:to-blue-150',
      shadow: 'shadow-blue-200',
      hoverShadow: 'hover:shadow-blue-300',
      text: 'text-blue-700'
    },
    'LTC': {
      primary: '#b91010ff',
      gradient: 'from-emerald-500 to-emerald-600',
      border: 'border-emerald-300',
      hoverBorder: 'hover:border-emerald-400',
      background: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
      hoverBackground: 'hover:from-emerald-100 hover:to-emerald-150',
      shadow: 'shadow-emerald-200',
      hoverShadow: 'hover:shadow-emerald-300',
      text: 'text-emerald-700'
    },
    'TPLUS': {
      primary: '#f6d25cff',
      gradient: 'from-violet-500 to-violet-600',
      border: 'border-violet-300',
      hoverBorder: 'hover:border-violet-400',
      background: 'bg-gradient-to-br from-violet-50 to-violet-100',
      hoverBackground: 'hover:from-violet-100 hover:to-violet-150',
      shadow: 'shadow-violet-200',
      hoverShadow: 'hover:shadow-violet-300',
      text: 'text-violet-700'
    },
    'ETL': {
      primary: '#07a0dcff',
      gradient: 'from-amber-500 to-amber-600',
      border: 'border-amber-300',
      hoverBorder: 'hover:border-amber-400',
      background: 'bg-gradient-to-br from-amber-50 to-amber-100',
      hoverBackground: 'hover:from-amber-100 hover:to-amber-150',
      shadow: 'shadow-amber-200',
      hoverShadow: 'hover:shadow-amber-300',
      text: 'text-amber-700'
    },
    'BEST': {
      primary: '#021264ff',
      gradient: 'from-rose-500 to-rose-600',
      border: 'border-rose-300',
      hoverBorder: 'hover:border-rose-400',
      background: 'bg-gradient-to-br from-rose-50 to-rose-100',
      hoverBackground: 'hover:from-rose-100 hover:to-rose-150',
      shadow: 'shadow-rose-200',
      hoverShadow: 'hover:shadow-rose-300',
      text: 'text-rose-700'
    }
  };

  // Get color scheme for operator
  const getOperatorColorScheme = (operatorName) => {
    return operatorColorSchemes[operatorName] || {
      primary: '#eef0f6ff',
      gradient: 'from-gray-500 to-gray-600',
      border: 'border-gray-300',
      hoverBorder: 'hover:border-gray-400',
      background: 'bg-gradient-to-br from-gray-50 to-gray-100',
      hoverBackground: 'hover:from-gray-100 hover:to-gray-150',
      shadow: 'shadow-gray-200',
      hoverShadow: 'hover:shadow-gray-300',
      text: 'text-gray-700'
    };
  };

  useEffect(() => {
    fetchOperators();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchOperators = async () => {
    try {
      setLoading(true);
      const response = await operatorAPI.getAll({ month: monthParam, year: yearParam });
      const operatorsData = response.data || [];
      
      // Process the data with proper field mapping for the actual API structure
      const processedOperators = operatorsData.map((op, index) => {
        // Ensure we have valid data
        const operatorName = op.operator_name || op.name || `Operator ${index + 1}`;
        const operatorCode = op.operator_code || op.code || `OP${index + 1}`;
        
        return {
          id: op.operator_id || op.id || index,
          name: operatorName,
          code: operatorCode,
          subscribers: parseInt(op.total_subscribers) || 0,
          activeSubscribers: parseInt(op.active_subscribers) || 0,
          color: op.color || '#3b82f6',
          logo: logoMap[operatorName] || null,
          compliance: parseFloat(op.avg_compliance_rate) || 0,
          revenue: parseFloat(op.total_monthly_revenue) || 0,
          marketPosition: op.market_position || 'Unknown',
          establishedYear: op.established_year || null
        };
      }).filter(op => op.name && op.name !== 'Operator'); // Filter out invalid operators

      setOperators(processedOperators);
      
      // Calculate totals
      const total = processedOperators.reduce((sum, op) => sum + op.subscribers, 0);
      const totalActive = processedOperators.reduce((sum, op) => sum + op.activeSubscribers, 0);
      
      setTotalSubscribers(total);
      setActiveRate(total > 0 ? (totalActive / total) * 100 : 0);
      
    } catch (err) {
      console.error('Error fetching operators:', err);
      setError(err.message || 'Failed to load operators data');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Operator Subscribers</h2>
        </div>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Loading operators data...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Operator Subscribers</h2>
        </div>
        <div className="flex justify-center items-center py-12">
          <AlertCircle className="h-8 w-8 text-red-500 mr-3" />
          <div className="text-center">
            <div className="text-red-600 font-medium">Error loading data</div>
            <div className="text-gray-500 text-sm mt-1">{error}</div>
            <button 
              onClick={fetchOperators}
              className="mt-3 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>
      
      <div className="relative z-10">
        <div className="mb-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Operator Subscribers Dashboard</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Real-time subscriber statistics and market share analysis across all telecommunications operators</p>
          </div>
          
          {/* Enhanced stats header */}
          <div className="flex flex-wrap justify-center items-center gap-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mr-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-sm text-gray-600 font-medium block">Total Subscribers</span>
                <span className="text-2xl font-bold text-gray-900">{formatNumber(totalSubscribers)}</span>
              </div>
            </div>
            
            <div className="w-px h-12 bg-gray-300"></div>
            
            <div className="flex items-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center mr-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-sm text-gray-600 font-medium block">Active Rate</span>
                <span className="text-2xl font-bold text-emerald-600">{activeRate.toFixed(1)}%</span>
              </div>
            </div>
            
            <div className="w-px h-12 bg-gray-300"></div>
            
            <div className="flex items-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center mr-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <div className="text-white text-sm font-bold">{operators.length}</div>
              </div>
              <div>
                <span className="text-sm text-gray-600 font-medium block">Operators</span>
                <span className="text-2xl font-bold text-purple-600">Active</span>
              </div>
            </div>
          </div>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {operators.map((operator, index) => {
          const colorScheme = getOperatorColorScheme(operator.name);
          return (
            <div
              key={operator.id || operator.name || index}
              className={`${colorScheme.background} ${colorScheme.hoverBackground} 
                         border-2 ${colorScheme.border} ${colorScheme.hoverBorder} 
                         rounded-xl p-5 transition-all duration-300 transform 
                         hover:scale-105 hover:shadow-lg ${colorScheme.shadow} ${colorScheme.hoverShadow}
                         backdrop-blur-sm relative overflow-hidden group`}
            >
              {/* Decorative gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.gradient} opacity-0 
                              group-hover:opacity-5 transition-opacity duration-300 rounded-xl`}></div>
              
              <div className="relative z-10">
                {/* Header with logo and compliance */}
                <div className="flex items-center justify-between mb-4">
                  <div className="relative">
                    <div className={`w-12 h-12 rounded-xl bg-white shadow-md border-2 ${colorScheme.border} 
                                   flex items-center justify-center p-2 group-hover:shadow-lg transition-all duration-300`}>
                      {operator.logo ? (
                        <img 
                          src={operator.logo} 
                          alt={`${operator.name} logo`}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-8 h-8 rounded-lg ${!operator.logo ? 'flex' : 'hidden'} items-center justify-center`} 
                        style={{backgroundColor: colorScheme.primary}}
                      >
                        <span className="text-white text-xs font-bold">
                          {operator.name && operator.name.length > 0 ? operator.name.charAt(0) : '?'}
                        </span>
                      </div>
                    </div>
                    {/* Decorative ring */}
                    <div className={`absolute -inset-1 bg-gradient-to-r ${colorScheme.gradient} rounded-xl opacity-20 
                                   group-hover:opacity-40 transition-opacity duration-300 -z-10`}></div>
                  </div>
                  
                  {/* Compliance badge with enhanced styling */}
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm border 
                                  ${operator.compliance >= 90 
                                    ? 'text-emerald-700 bg-emerald-100 border-emerald-200' 
                                    : operator.compliance >= 80 
                                    ? 'text-amber-700 bg-amber-100 border-amber-200'
                                    : 'text-red-700 bg-red-100 border-red-200'
                                  } transition-all duration-300 group-hover:shadow-md`}>
                    {operator.compliance ? `${operator.compliance.toFixed(1)}%` : 'N/A'}
                  </div>
                </div>
                
                {/* Content */}
                <div className="text-center mb-4">
                  <h3 className={`text-lg font-bold ${colorScheme.text} mb-2 group-hover:text-opacity-80 transition-colors duration-300`}>
                    {operator.name || 'Unknown'}
                  </h3>
                  <div className="relative">
                    <p className={`text-3xl font-black ${colorScheme.text} mb-1 relative z-10`}>
                      {formatNumber(operator.subscribers)}
                    </p>
                    {/* Number background glow effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${colorScheme.gradient} opacity-0 
                                   group-hover:opacity-10 blur-lg transition-all duration-300 rounded-lg`}></div>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Subscribers</p>
                </div>

                {/* Enhanced progress bar */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-600">Market Share</span>
                    <span className={`text-xs font-bold ${colorScheme.text}`}>
                      {totalSubscribers > 0 ? ((operator.subscribers / totalSubscribers) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden`}
                        style={{
                          width: `${totalSubscribers > 0 ? (operator.subscribers / totalSubscribers) * 100 : 0}%`,
                          background: `linear-gradient(90deg, ${colorScheme.primary}, ${colorScheme.primary}dd)`
                        }}
                      >
                        {/* Shimmer effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent 
                                       opacity-30 transform -translate-x-full group-hover:translate-x-full 
                                       transition-transform duration-1000 ease-in-out"></div>
                      </div>
                    </div>
                    {/* Progress glow */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${colorScheme.gradient} opacity-0 
                                   group-hover:opacity-20 blur rounded-full transition-opacity duration-300`}></div>
                  </div>
                </div>

                {/* Additional stats */}
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-center">
                    <div className={`text-lg font-bold ${colorScheme.text}`}>
                      {formatNumber(operator.activeSubscribers)}
                    </div>
                    <div className="text-xs text-gray-600">Active</div>
                  </div>
                  <div className="w-px h-8 bg-gray-300"></div>
                  <div className="text-center">
                    <div className={`text-lg font-bold text-gray-600`}>
                      {formatNumber(operator.subscribers - operator.activeSubscribers)}
                    </div>
                    <div className="text-xs text-gray-600">Inactive</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Enhanced Partner Network Showcase */}
      <div className="mt-8 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-slate-200 shadow-lg">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Partner Network</h3>
          <p className="text-gray-600">Our trusted telecommunications partners across Lao PDR</p>
        </div>
        
        <div className="flex justify-center items-center flex-wrap gap-6">
          {operators.map((operator) => {
            const colorScheme = getOperatorColorScheme(operator.name);
            return (
              <div key={`logo-${operator.id}`} className="group cursor-pointer">
                <div className="relative">
                  <div className={`w-20 h-20 bg-white rounded-2xl shadow-lg border-2 ${colorScheme.border} 
                                 hover:shadow-2xl transition-all duration-300 group-hover:scale-110 
                                 flex items-center justify-center p-3 relative overflow-hidden`}>
                    {/* Background gradient on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.gradient} opacity-0 
                                   group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    {operator.logo ? (
                      <img 
                        src={operator.logo} 
                        alt={`${operator.name} logo`}
                        className="w-12 h-12 object-contain relative z-10 transition-transform duration-300 group-hover:scale-110"
                        title={`${operator.name} - ${formatNumber(operator.subscribers)} subscribers`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-12 h-12 rounded-xl ${!operator.logo ? 'flex' : 'hidden'} items-center justify-center relative z-10`} 
                      style={{background: `linear-gradient(135deg, ${colorScheme.primary}, ${colorScheme.primary}dd)`}}
                    >
                      <span className="text-white text-sm font-bold">
                        {operator.name && operator.name.length > 0 ? operator.name.substring(0, 2) : 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Decorative ring */}
                  <div className={`absolute -inset-1 bg-gradient-to-br ${colorScheme.gradient} rounded-2xl opacity-0 
                                 group-hover:opacity-30 transition-opacity duration-300 -z-10 blur-sm`}></div>
                </div>
                
                {/* Enhanced operator info */}
                <div className="text-center mt-3">
                  <p className={`font-bold text-sm ${colorScheme.text} group-hover:text-opacity-80 transition-colors duration-300`}>
                    {operator.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatNumber(operator.subscribers)} users
                  </p>
                  
                  {/* Mini progress indicator */}
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className="h-1 rounded-full transition-all duration-500"
                        style={{
                          width: `${totalSubscribers > 0 ? (operator.subscribers / totalSubscribers) * 100 : 0}%`,
                          background: `linear-gradient(90deg, ${colorScheme.primary}, ${colorScheme.primary}aa)`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Enhanced Summary Statistics */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Active Subscribers Card */}
        <div className="relative group">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200 
                        rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg 
                        hover:border-emerald-300 transform hover:scale-105 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-200 rounded-full -translate-y-8 translate-x-8 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-emerald-300 rounded-full translate-y-6 -translate-x-6 opacity-20"></div>
            
            <div className="relative z-10">
              <div className="mb-3">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl mx-auto flex items-center justify-center shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-black text-emerald-700 mb-2">
                {formatNumber(Math.round(totalSubscribers * (activeRate / 100)))}
              </div>
              <div className="text-sm text-emerald-800 font-semibold">Active Subscribers</div>
              <div className="text-xs text-emerald-600 mt-1">{activeRate.toFixed(1)}% active rate</div>
            </div>
          </div>
        </div>

        {/* Inactive Subscribers Card */}
        <div className="relative group">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 
                        rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg 
                        hover:border-slate-300 transform hover:scale-105 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-16 h-16 bg-slate-200 rounded-full -translate-y-8 translate-x-8 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-slate-300 rounded-full translate-y-6 -translate-x-6 opacity-20"></div>
            
            <div className="relative z-10">
              <div className="mb-3">
                <div className="w-12 h-12 bg-slate-500 rounded-xl mx-auto flex items-center justify-center shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-black text-slate-700 mb-2">
                {formatNumber(Math.round(totalSubscribers * ((100 - activeRate) / 100)))}
              </div>
              <div className="text-sm text-slate-800 font-semibold">Inactive Subscribers</div>
              <div className="text-xs text-slate-600 mt-1">{(100 - activeRate).toFixed(1)}% inactive rate</div>
            </div>
          </div>
        </div>

        {/* Average Compliance Card */}
        <div className="relative group">
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200 
                        rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg 
                        hover:border-amber-300 transform hover:scale-105 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-16 h-16 bg-amber-200 rounded-full -translate-y-8 translate-x-8 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-amber-300 rounded-full translate-y-6 -translate-x-6 opacity-20"></div>
            
            <div className="relative z-10">
              <div className="mb-3">
                <div className="w-12 h-12 bg-amber-500 rounded-xl mx-auto flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-black text-amber-700 mb-2">
                {operators.length > 0 ? (operators.reduce((sum, op) => sum + (op.compliance || 0), 0) / operators.length).toFixed(1) : 0}%
              </div>
              <div className="text-sm text-amber-800 font-semibold">Avg Compliance</div>
              <div className="text-xs text-amber-600 mt-1">across all operators</div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default OperatorSubscribers;